/* eslint-disable camelcase */
import { ReactNode, useEffect, useRef, useState } from 'react';
import { alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { ArticleOutlined } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { isForbundCustomer } from 'src/modules/sending/functions/isForbundCustomer';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import invoiceMissionService from 'src/shared/services/invoiceMissionService';
import { InvoiceMissionOptions } from 'src/shared/models/options/invoiceMissionOptions';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { getInvoiceTemplateById } from 'src/shared/services/invoiceService';
import { InvoiceTemplate } from 'src/modules/invoice/models/invoiceTemplate';
import { SendingPreviousMissionPicker } from 'src/modules/sending/components/sendingPreviousMissionPicker';
import { useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';

// De fyra texterna som kan hämtas från en tidigare avisering.
interface MissionTexts {
  emailHeading: string;
  emailText: string;
  invoiceHeading: string;
  invoiceText: string;
  // Den tidigare aviseringens logotypval (false = förbundets logga, true = föreningens egna logga) – ärvs vid kopiering.
  useLocalAssLogo: boolean;
}

// Plockar ut e-post- och fakturatexterna ur mallens sendings. Vilka rader som gäller beror på utskicksledet:
// huvudutskicket har Email (e-post/Kivra) + Main (fakturans innehåll), medan en påminnelse har allt på sin
// egen Reminder-rad (både brevtexten och e-posttexten).
const extractTexts = (template: InvoiceTemplate, led: number): MissionTexts => {
  const sendings = template.sendings ?? [];

  if (led !== 0) {
    const type = led === 2 ? InvoiceSendingTypeIntegerEnum.Reminder2 : InvoiceSendingTypeIntegerEnum.Reminder1;
    const reminder = sendings.find((s) => s.sendingType === type);
    return {
      emailHeading: reminder?.emailHeading || '',
      emailText: reminder?.emailText || '',
      invoiceHeading: reminder?.defaultHeading || '',
      invoiceText: reminder?.defaultText || '',
      useLocalAssLogo: !!template.useLocalAssLogo,
    };
  }

  const email = sendings.find((s) => s.sendingType === InvoiceSendingTypeIntegerEnum.Email);
  const main = sendings.find((s) => s.sendingType === InvoiceSendingTypeIntegerEnum.Main);
  return {
    emailHeading: email?.emailHeading || email?.defaultHeading || '',
    emailText: email?.emailText || email?.defaultText || '',
    invoiceHeading: main?.defaultHeading || '',
    invoiceText: main?.defaultText || '',
    useLocalAssLogo: !!template.useLocalAssLogo,
  };
};

// En rubrik + textstycke (renderad HTML) eller tomtext när inget angetts.
const textBlock = (label: string, heading: string, html: string): ReactNode => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>
      {label}
    </Typography>
    {heading || html ? (
      <>
        {heading && (
          <Typography variant='body2' sx={{ fontWeight: 700, mt: 0.5 }}>
            {heading}
          </Typography>
        )}
        {html && (
          // eslint-disable-next-line react/no-danger
          <Typography
            variant='body2'
            color='text.secondary'
            component='div'
            // Innesluter kundens egen CSS (t.ex. vattenmärken med position: fixed) i rutan.
            sx={{ mt: 0.5, maxHeight: 140, overflow: 'auto', contain: 'paint', position: 'relative' }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </>
    ) : (
      <Typography variant='body2' color='text.disabled' sx={{ mt: 0.5 }}>
        Ingen text
      </Typography>
    )}
  </Box>
);

interface SendingCopyTextsFromMissionModalProps {
  open: boolean;
  onClose: () => void;
  // Om utskicket går via e-post/Kivra – styr om e-posttexten visas/kopieras.
  showEmail: boolean;
}

// Modal: hämta texterna (e-post/Kivra + fakturans innehåll) från en tidigare avisering, likt profil-jämförelsen i steg 2.
// Vänster = den tidigare aviseringens texter, höger = mina nuvarande texter. "Kopiera texterna" fyller i formuläret.
export const SendingCopyTextsFromMissionModal = ({ open, onClose, showEmail }: SendingCopyTextsFromMissionModalProps) => {
  const isMounted = useIsMounted();
  const { watch, setValue } = useFormContext();
  const { currentUser } = useGlobals();
  const ownerId = watch('owner_id') as number | undefined;

  // Logotypvalet finns bara för ett förbund (flera föreningar). Ett förbund ärver den kopierade aviseringens val fritt;
  // en enskild förening har inget val och använder alltid förbundets (centrala) logga (useLocalAssLogo = false).
  const forbundCustomer = isForbundCustomer(currentUser);

  // Etikett för meddelande-blocket anpassas efter valda kanaler: bara e-post, bara Kivra, eller båda.
  const deliveryOrder = ((watch('deliveryMethodOrder') as string) ?? '').split(',');
  const hasMail = deliveryOrder.includes('mail');
  const hasKivra = deliveryOrder.includes('kivra');
  let emailChannelLabel = 'E-post';
  if (hasMail && hasKivra) emailChannelLabel = 'E-post / Kivra';
  else if (hasKivra && !hasMail) emailChannelLabel = 'Kivra';

  // Aviseringen som urvalet kopierades från (steg 2) – förväljs här så man slipper välja avisering igen.
  const { copyFromMissionId, led } = useSendingWizard();

  const [missions, setMissions] = useState<InvoiceMissionResponse[]>([]);
  const [selected, setSelected] = useState<InvoiceMissionResponse | null>(null);
  const [prev, setPrev] = useState<MissionTexts | null>(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  // Väljaren döljs när urvalets avisering förvalts; "Byt avisering" visar den igen.
  const [showPicker, setShowPicker] = useState(false);
  const autoSelectedRef = useRef(false);

  // Hämta Medlemsavisering-aviseringar för faktureringsgruppen när modalen öppnas.
  useEffect(() => {
    if (!open) return;
    (async () => {
      setListLoading(true);
      try {
        const opts = new InvoiceMissionOptions();
        opts.pageIndex = 0;
        // Hämtar bara de 10 senaste (sorterat fallande) för att hålla laddningstiden nere hos kunder med många aviseringar.
        opts.pageSize = 10;
        if (ownerId) opts.ownerId = ownerId;
        const res = await invoiceMissionService.getAll(opts);
        if (isMounted()) setMissions((res.data.results ?? []).filter((m) => m.type === InvoiceMissionTypeTextEnum.member));
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setListLoading(false);
      }
    })();
  }, [open, ownerId, isMounted]);

  const handleClose = () => {
    setSelected(null);
    setPrev(null);
    onClose();
  };

  const onSelect = async (m: InvoiceMissionResponse | null) => {
    setSelected(m);
    setPrev(null);
    if (!m || !m.template_id) return;
    setLoading(true);
    try {
      const res = await getInvoiceTemplateById(m.template_id);
      if (isMounted()) setPrev(extractTexts(res.data, led));
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  // När modalen stängs: nollställ auto-val-flaggan så nästa öppning kan förvälja igen.
  useEffect(() => {
    if (!open) autoSelectedRef.current = false;
  }, [open]);

  // Förvälj den avisering urvalet kopierades från (om någon) när listan laddats, och hoppa direkt till texterna –
  // väljaren döljs (nås via "Byt avisering"). Finns ingen förvald avisering (eller den saknas i listan) visas väljaren.
  useEffect(() => {
    if (!open || autoSelectedRef.current) return;
    if (copyFromMissionId == null) {
      setShowPicker(true);
      return;
    }
    const found = missions.find((m) => m.invoiceMission_id === copyFromMissionId);
    if (found) {
      autoSelectedRef.current = true;
      setShowPicker(false);
      onSelect(found);
    } else if (missions.length > 0) {
      autoSelectedRef.current = true;
      setShowPicker(true);
    }
  }, [open, missions, copyFromMissionId, onSelect]);

  // Kopiera den tidigare aviseringens texter till formuläret (e-posttexten bara om utskicket går via e-post/Kivra).
  const apply = () => {
    if (!prev) return;
    if (showEmail) {
      setValue('emailHeading', prev.emailHeading, { shouldValidate: true });
      setValue('emailText', prev.emailText, { shouldValidate: true });
    }
    setValue('invoiceHeading', prev.invoiceHeading, { shouldValidate: true });
    setValue('invoiceText', prev.invoiceText, { shouldValidate: true });
    // Ett förbund ärver logotypvalet från den kopierade aviseringen; en enskild förening har inget val → förbundets logga.
    setValue('useLocalAssLogo', forbundCustomer ? prev.useLocalAssLogo : false);
    // Markera att texterna hämtats från en tidigare avisering (styr vilken lägesknapp som är aktiv i steg 4).
    setValue('textsFromPrevious', true);
    setValue('completeLater', false);
    handleClose();
  };

  const current: MissionTexts = {
    emailHeading: (watch('emailHeading') as string) ?? '',
    emailText: (watch('emailText') as string) ?? '',
    invoiceHeading: (watch('invoiceHeading') as string) ?? '',
    invoiceText: (watch('invoiceText') as string) ?? '',
    useLocalAssLogo: watch('useLocalAssLogo') === true,
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Hämta texter från tidigare avisering</DialogTitle>
      <DialogContent dividers>
        {showPicker ? (
          <SendingPreviousMissionPicker
            missions={missions}
            selectedId={selected?.invoiceMission_id ?? null}
            onSelect={onSelect}
            emptyText='Inga tidigare medlemsaviseringar'
            limitNote='Visar de 10 senaste aviseringarna.'
            loading={listLoading}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant='body2' color='text.secondary'>
              Texter hämtas från: <b>{selected?.missionName ?? 'den avisering du valde i urvalet'}</b>
            </Typography>
            <Button variant='text' size='small' onClick={() => setShowPicker(true)}>
              Byt avisering
            </Button>
          </Box>
        )}

        {loading && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!prev && !loading && (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
            <ArticleOutlined sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography variant='body2'>Välj en avisering ovan för att se dess texter.</Typography>
          </Box>
        )}

        {prev && !loading && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
              {/* Vänster: den tidigare aviseringens texter */}
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 700, color: 'text.secondary', pb: 1, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  Tidigare avisering
                </Typography>
                {showEmail && textBlock(emailChannelLabel, prev.emailHeading, prev.emailText)}
                {textBlock('Fakturans innehåll', prev.invoiceHeading, prev.invoiceText)}
              </Box>

              {/* Höger: mina nuvarande texter (endast utskrift) */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                }}
              >
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 700, color: 'primary.main', pb: 1, mb: 1.5, borderBottom: '1px solid', borderColor: 'primary.light' }}
                >
                  Nuvarande avisering
                </Typography>
                {showEmail && textBlock(emailChannelLabel, current.emailHeading, current.emailText)}
                {textBlock('Fakturans innehåll', current.invoiceHeading, current.invoiceText)}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color='inherit' onClick={handleClose}>
          Stäng
        </Button>
        <Button variant='contained' disableElevation onClick={apply} disabled={!prev}>
          Kopiera texterna
        </Button>
      </DialogActions>
    </Dialog>
  );
};
